import { useMemo, useState } from 'react';

import { Card, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatInt } from 'scenes/VisitHistory/utils/format';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

import { IncidentGroupBy, buildIncidentRanking } from '../utils/storeVisits';

const I18N_BASE = 'scenes.store_visits.incidents';
const CSS = 'visit-history-inc';

interface StoreVisitsIncidentsProps {
  stops: TiendaPerfectaVisitHistoryStop[];
}

/** Top 5 de incidencias con bypass, por tipo o por distribuidor. */
function StoreVisitsIncidents({ stops }: StoreVisitsIncidentsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [groupBy, setGroupBy] = useState<IncidentGroupBy>('type');
  const noReason = t(`${I18N_BASE}.no_reason`);

  const ranking = useMemo(
    () => buildIncidentRanking(stops, groupBy, noReason),
    [stops, groupBy, noReason]
  );
  const maxCount = ranking.top[0]?.count ?? 1;

  return (
    <Card
      className={CSS}
      title={
        <>
          {t(`${I18N_BASE}.title`)}
          <span className={`${CSS}__sub`}>
            {t(`${I18N_BASE}.subtitle`, {
              total: formatInt(ranking.total, language),
              count: ranking.top.length,
            })}
          </span>
        </>
      }
      extra={
        <Segmented
          size="small"
          value={groupBy}
          onChange={(value) => setGroupBy(value as IncidentGroupBy)}
          options={[
            { label: t(`${I18N_BASE}.by.type`), value: 'type' },
            { label: t(`${I18N_BASE}.by.distributor`), value: 'distributor' },
          ]}
        />
      }
    >
      {ranking.top.length === 0 ? (
        <div className={`${CSS}__empty`}>{t(`${I18N_BASE}.empty`)}</div>
      ) : (
        <ol className={`${CSS}__list`}>
          {ranking.top.map((row, index) => (
            <li key={row.key}>
              <div className={`${CSS}__row`}>
                <span className={`${CSS}__rank`}>{index + 1}</span>
                <span className={`${CSS}__name`} title={row.label}>
                  {row.label}
                </span>
                <span className={`${CSS}__count`}>
                  {formatInt(row.count, language)}
                </span>
                <span className={`${CSS}__pct`}>{`${row.pct}%`}</span>
                <span className={`${CSS}__bar`}>
                  <span style={{ width: `${(row.count / maxCount) * 100}%` }} />
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
      {groupBy === 'distributor' && ranking.top.length > 0 && (
        <div className={`${CSS}__more`}>
          {t(`${I18N_BASE}.distributor_note`)}
        </div>
      )}
      {ranking.restGroups > 0 && (
        <div className={`${CSS}__more`}>
          {t(`${I18N_BASE}.more_${groupBy}`, {
            count: ranking.restGroups,
            incidents: formatInt(ranking.restCount, language),
          })}
        </div>
      )}
    </Card>
  );
}

export default StoreVisitsIncidents;
